import { ChevronDown, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BuilderCtaCard } from '../components/BuilderCtaCard';
import { ListingCard } from '../components/ListingCard';
import { compareListingDisplayPriority } from '../lib/marketplace';

const LISTINGS_PER_PAGE = 6;

const SORT_OPTIONS = [
  { value: 'best-selling', label: 'Best selling' },
  { value: 'top-rated', label: 'Top rated' },
  { value: 'newest', label: 'Newest arrivals' },
  { value: 'price-low', label: 'Price: low to high' },
  { value: 'price-high', label: 'Price: high to low' },
  { value: 'fast-delivery', label: 'Fastest delivery' },
];

const BUDGET_OPTIONS = [
  { value: 'all', title: 'All budgets', meta: 'Show every listing' },
  { value: 'under-25', title: 'Under $25', meta: '$0 to $25' },
  { value: '25-50', title: '$25 - $50', meta: '$25 to $50' },
  { value: '50-100', title: '$50 - $100', meta: '$50 to $100' },
  { value: '100-250', title: '$100 - $250', meta: '$100 to $250' },
  { value: '250-500', title: '$250 - $500', meta: '$250 to $500' },
  { value: '500-plus', title: '$500+', meta: '$500 and above' },
];

function parseCategorySlugs(searchParams) {
  const pluralParam = searchParams.get('categories');
  if (pluralParam) {
    return pluralParam
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  const singularParam = searchParams.get('category');
  return singularParam && singularParam !== 'all' ? [singularParam] : [];
}

function parseBudgetCap(searchParams) {
  const raw = Number(searchParams.get('maxPrice'));
  return Number.isFinite(raw) && raw > 0 ? raw : null;
}

function getBudgetRange(value) {
  switch (value) {
    case 'under-25':
      return { min: 0, max: 25 };
    case '25-50':
      return { min: 25, max: 50 };
    case '50-100':
      return { min: 50, max: 100 };
    case '100-250':
      return { min: 100, max: 250 };
    case '250-500':
      return { min: 250, max: 500 };
    case '500-plus':
      return { min: 500, max: null };
    case 'all':
    default:
      return null;
  }
}

function formatBudgetValue(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function clampPage(value, pageCount) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return 1;
  }

  return Math.min(Math.floor(parsed), pageCount);
}

function getPaginationItems(currentPage, pageCount) {
  if (pageCount <= 5) {
    return Array.from({ length: pageCount }, (_, index) => index + 1);
  }

  const items = [1];
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(pageCount - 1, currentPage + 1);

  if (start > 2) {
    items.push('gap-start');
  }

  for (let page = start; page <= end; page += 1) {
    items.push(page);
  }

  if (end < pageCount - 1) {
    items.push('gap-end');
  }

  items.push(pageCount);
  return items;
}

function FilterMenuButton({ label, active, isOpen, onClick }) {
  return (
    <button
      type="button"
      className={`filter-menu-button${active ? ' active' : ''}${isOpen ? ' open' : ''}`}
      onClick={onClick}
    >
      <span>{label}</span>
      <ChevronDown size={16} />
    </button>
  );
}

function ExploreLoadingSkeleton() {
  return (
    <section className="page-shell explore-page">
      <div className="explore-loading-hero" aria-hidden="true">
        <span className="skeleton-line skeleton-kicker" />
        <span className="skeleton-line skeleton-title" />
        <span className="skeleton-line skeleton-copy" />
      </div>

      <div className="marketplace-filters explore-loading-filters" aria-hidden="true">
        <span className="skeleton-block skeleton-search" />
        <span className="skeleton-block skeleton-filter" />
        <span className="skeleton-block skeleton-filter" />
      </div>

      <div className="listing-grid explore-loading-grid" aria-label="Loading listings">
        {Array.from({ length: LISTINGS_PER_PAGE }, (_, index) => (
          <article key={index} className="listing-card simple-listing-card listing-card-skeleton">
            <span className="skeleton-cover" />
            <div className="listing-card-content">
              <span className="skeleton-line skeleton-card-title" />
              <span className="skeleton-line" />
              <span className="skeleton-line skeleton-short" />
            </div>
            <div className="listing-card-footer">
              <span className="skeleton-line skeleton-price" />
              <span className="skeleton-line skeleton-link" />
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

export function ExplorePage({ snapshot, loading }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [openMenu, setOpenMenu] = useState(null);
  const filtersRef = useRef(null);
  const resultsRef = useRef(null);
  const searchParamsKey = searchParams.toString();

  const query = searchParams.get('q') || '';
  const selectedCategorySlugs = useMemo(
    () => parseCategorySlugs(new URLSearchParams(searchParamsKey)),
    [searchParamsKey]
  );
  const budget = searchParams.get('budget') || 'all';
  const budgetMax = parseBudgetCap(searchParams);
  const sortBy = searchParams.get('sort') || 'best-selling';
  const budgetSliderMax = useMemo(() => {
    const listingMax = snapshot?.listings.reduce(
      (currentMax, listing) => Math.max(currentMax, Number(listing.priceFrom) || 0),
      0
    );
    return Math.max(5000, Math.ceil((listingMax || 5000) / 500) * 500);
  }, [snapshot]);

  const [draftCategories, setDraftCategories] = useState(selectedCategorySlugs);
  const [draftBudget, setDraftBudget] = useState(budget);
  const [draftBudgetMax, setDraftBudgetMax] = useState(budgetMax ?? budgetSliderMax);

  useEffect(() => {
    setDraftCategories(selectedCategorySlugs);
    setDraftBudget(budget);
    setDraftBudgetMax(budgetMax ?? budgetSliderMax);
  }, [budget, budgetMax, budgetSliderMax, selectedCategorySlugs]);

  useEffect(() => {
    function handlePointerDown(event) {
      if (filtersRef.current && !filtersRef.current.contains(event.target)) {
        setOpenMenu(null);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  const filteredListings = useMemo(() => {
    if (!snapshot) {
      return [];
    }

    const visible = snapshot.listings.filter((listing) => {
      const isPublic = (listing.status || 'published') === 'published' && (listing.visibility || 'public') === 'public';
      const matchesQuery =
        query.length === 0 ||
        [listing.title, listing.tagline, listing.summary, listing.category?.name, ...(listing.useCases || [])]
          .join(' ')
          .toLowerCase()
          .includes(query.toLowerCase());

      const matchesCategory =
        selectedCategorySlugs.length === 0 || selectedCategorySlugs.includes(listing.category?.slug);

      const matchesBudget = (() => {
        if (budgetMax !== null) {
          return listing.priceFrom <= budgetMax;
        }

        const budgetRange = getBudgetRange(budget);
        if (!budgetRange) {
          return true;
        }

        const withinMin = listing.priceFrom >= budgetRange.min;
        const withinMax = budgetRange.max === null || listing.priceFrom <= budgetRange.max;
        return withinMin && withinMax;
      })();

      return isPublic && matchesQuery && matchesCategory && matchesBudget;
    });

    return [...visible].sort((left, right) => {
      const displayPriority = compareListingDisplayPriority(left, right);

      if (displayPriority !== 0) {
        return displayPriority;
      }

      switch (sortBy) {
        case 'top-rated':
          return right.rating - left.rating || right.reviewCount - left.reviewCount;
        case 'newest':
          return new Date(right.createdAt) - new Date(left.createdAt);
        case 'price-low':
          return left.priceFrom - right.priceFrom;
        case 'price-high':
          return right.priceFrom - left.priceFrom;
        case 'fast-delivery':
          return left.deliveryDays - right.deliveryDays || left.priceFrom - right.priceFrom;
        case 'best-selling':
        default:
          return right.ordersCompleted - left.ordersCompleted || right.rating - left.rating;
      }
    });
  }, [budget, budgetMax, query, selectedCategorySlugs, snapshot, sortBy]);

  const pageCount = Math.max(1, Math.ceil(filteredListings.length / LISTINGS_PER_PAGE));
  const currentPage = clampPage(searchParams.get('page'), pageCount);
  const pageStart = (currentPage - 1) * LISTINGS_PER_PAGE;
  const visibleListings = filteredListings.slice(pageStart, pageStart + LISTINGS_PER_PAGE);
  const pageItems = useMemo(() => getPaginationItems(currentPage, pageCount), [currentPage, pageCount]);

  const activePills = useMemo(() => {
    const pills = [];

    if (selectedCategorySlugs.length > 0) {
      selectedCategorySlugs.forEach((slug) => {
        const category = snapshot?.categories.find((entry) => entry.slug === slug);
        if (category) {
          pills.push({ key: `category-${slug}`, label: category.name });
        }
      });
    }

    if (budgetMax !== null) {
      pills.push({ key: `max-price-${budgetMax}`, label: `Up to ${formatBudgetValue(budgetMax)}` });
    } else if (budget !== 'all') {
      const budgetOption = BUDGET_OPTIONS.find((entry) => entry.value === budget);
      if (budgetOption) {
        pills.push({ key: `budget-${budget}`, label: budgetOption.title });
      }
    }

    return pills;
  }, [budget, budgetMax, selectedCategorySlugs, snapshot?.categories]);

  function updateSearchParam(nextMutator, options = {}) {
    const { resetPage = true } = options;
    const next = new URLSearchParams(searchParams);
    nextMutator(next);
    if (resetPage) {
      next.delete('page');
    }
    setSearchParams(next);
  }

  function applyCategoryFilters() {
    updateSearchParam((next) => {
      next.delete('category');
      if (draftCategories.length === 0) {
        next.delete('categories');
      } else {
        next.set('categories', draftCategories.join(','));
      }
    });
    setOpenMenu(null);
  }

  function applyBudgetFilter() {
    updateSearchParam((next) => {
      if (draftBudgetMax < budgetSliderMax) {
        next.set('maxPrice', String(draftBudgetMax));
        next.delete('budget');
      } else {
        next.delete('maxPrice');
        if (draftBudget === 'all') {
          next.delete('budget');
        } else {
          next.set('budget', draftBudget);
        }
      }
    });
    setOpenMenu(null);
  }

  function clearAllFilters() {
    const next = new URLSearchParams(searchParams);
    ['categories', 'category', 'budget', 'maxPrice', 'page'].forEach((key) => next.delete(key));
    setSearchParams(next);
    setOpenMenu(null);
  }

  function resetExploreSearch() {
    const next = new URLSearchParams(searchParams);
    ['q', 'categories', 'category', 'budget', 'maxPrice', 'page'].forEach((key) => next.delete(key));
    setSearchParams(next);
    setOpenMenu(null);
  }

  function goToPage(nextPage) {
    const boundedPage = clampPage(nextPage, pageCount);
    updateSearchParam(
      (next) => {
        if (boundedPage <= 1) {
          next.delete('page');
        } else {
          next.set('page', String(boundedPage));
        }
      },
      { resetPage: false }
    );
    resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  if (loading || !snapshot) {
    return <ExploreLoadingSkeleton />;
  }

  return (
    <section className="page-shell explore-page">
      <div className="explore-hero">
        <div>
          <span className="eyebrow left">Explore services</span>
          <h1>Browse AI agent services</h1>
          <p>Compare specialized agents the same way you would compare services on Fiverr.</p>
        </div>
      </div>

      <div className="marketplace-filters" ref={filtersRef}>
        <div className="filters-bar">
          <label className="filter-control filter-search">
            <Search size={16} />
            <input
              className="ui-search-input"
              type="text"
              value={query}
              placeholder="Try 'support automation' or 'AI SDR'"
              onChange={(event) => {
                updateSearchParam((next) => {
                  if (event.target.value) {
                    next.set('q', event.target.value);
                  } else {
                    next.delete('q');
                  }
                });
              }}
            />
          </label>

          <div className="filter-menu-group">
            <div className="filter-menu">
              <FilterMenuButton
                label="Service options"
                active={selectedCategorySlugs.length > 0}
                isOpen={openMenu === 'service'}
                onClick={() => setOpenMenu((current) => (current === 'service' ? null : 'service'))}
              />

              {openMenu === 'service' ? (
                <div className="filter-popover filter-popover-wide">
                  <div className="filter-popover-scroll">
                    <div className="filter-popover-section">
                      <h3>Agent categories</h3>
                      <div className="filter-option-grid">
                        {snapshot.categories.map((category) => (
                          <label key={category.id} className="filter-option-row">
                            <input
                              type="checkbox"
                              checked={draftCategories.includes(category.slug)}
                              onChange={() =>
                                setDraftCategories((current) =>
                                  current.includes(category.slug)
                                    ? current.filter((slug) => slug !== category.slug)
                                    : [...current, category.slug]
                                )
                              }
                            />
                            <span>{category.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="filter-popover-actions">
                    <button type="button" className="filter-clear-button" onClick={() => setDraftCategories([])}>
                      Clear all
                    </button>
                    <button type="button" className="filter-apply-button" onClick={applyCategoryFilters}>
                      Apply
                    </button>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="filter-menu">
              <FilterMenuButton
                label="Budget"
                active={budget !== 'all' || budgetMax !== null}
                isOpen={openMenu === 'budget'}
                onClick={() => setOpenMenu((current) => (current === 'budget' ? null : 'budget'))}
              />

              {openMenu === 'budget' ? (
                <div className="filter-popover">
                  <div className="filter-popover-scroll">
                    <div className="filter-popover-section">
                      <h3>Budget reflects the base price.</h3>
                      <div className="filter-option-stack">
                        {BUDGET_OPTIONS.map((option) => (
                          <label key={option.value} className="filter-radio-row">
                            <input
                              type="radio"
                              name="budget-filter"
                              checked={draftBudget === option.value}
                              onChange={() => {
                                setDraftBudget(option.value);
                                setDraftBudgetMax(budgetSliderMax);
                              }}
                            />
                            <div>
                              <strong>{option.title}</strong>
                              <span>{option.meta}</span>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="filter-popover-section budget-range-panel">
                      <div className="budget-range-head">
                        <h3>Custom</h3>
                        <strong className="budget-range-value">
                          {draftBudgetMax < budgetSliderMax
                            ? `Up to ${formatBudgetValue(draftBudgetMax)}`
                            : 'No cap'}
                        </strong>
                      </div>
                      <span className="budget-range-label">Up to</span>
                      <input
                        className="budget-range-input"
                        type="range"
                        min="500"
                        max={budgetSliderMax}
                        step="250"
                        value={draftBudgetMax}
                        onChange={(event) => {
                          setDraftBudget('all');
                          setDraftBudgetMax(Number(event.target.value));
                        }}
                      />
                      <div className="budget-range-scale" aria-hidden="true">
                        <span>{formatBudgetValue(500)}</span>
                        <span>{formatBudgetValue(budgetSliderMax)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="filter-popover-actions">
                    <button
                      type="button"
                      className="filter-clear-button"
                      onClick={() => {
                        setDraftBudget('all');
                        setDraftBudgetMax(budgetSliderMax);
                      }}
                    >
                      Clear all
                    </button>
                    <button type="button" className="filter-apply-button" onClick={applyBudgetFilter}>
                      Apply
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="explore-summary" ref={resultsRef}>
        <div className="explore-summary-count">
          <strong>{filteredListings.length}</strong>
          <span>{filteredListings.length === 1 ? 'service available' : 'services available'}</span>
        </div>

        <div className="results-pills">
          {activePills.length > 0 ? (
            <>
              {activePills.map((pill) => (
                <span key={pill.key} className="tag-pill active">
                  {pill.label}
                </span>
              ))}
              {selectedCategorySlugs.length > 0 ? (
                <button
                  type="button"
                  className="pill-clear-button"
                  onClick={() => {
                    updateSearchParam((next) => {
                      next.delete('categories');
                      next.delete('category');
                    });
                  }}
                >
                  <span aria-hidden="true">&times;</span>
                  Clear
                </button>
              ) : null}
            </>
          ) : (
            snapshot.categories.slice(0, 5).map((category) => (
              <button
                key={category.id}
                type="button"
                className="tag-pill"
                onClick={() => {
                  updateSearchParam((next) => {
                    next.delete('category');
                    next.set('categories', category.slug);
                  });
                }}
              >
                {category.name}
              </button>
            ))
          )}
        </div>

        <div className="explore-sort">
          <span>Sort by:</span>
          <div className="filter-menu">
            <FilterMenuButton
              label={SORT_OPTIONS.find((option) => option.value === sortBy)?.label || 'Best selling'}
              active={sortBy !== 'best-selling'}
              isOpen={openMenu === 'sort'}
              onClick={() => setOpenMenu((current) => (current === 'sort' ? null : 'sort'))}
            />

            {openMenu === 'sort' ? (
              <div className="filter-popover filter-popover-compact filter-popover-right">
                <div className="filter-popover-scroll">
                  <div className="filter-option-stack">
                    {SORT_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`sort-option${sortBy === option.value ? ' active' : ''}`}
                        onClick={() => {
                          updateSearchParam((next) => {
                            if (option.value === 'best-selling') {
                              next.delete('sort');
                            } else {
                              next.set('sort', option.value);
                            }
                          });
                          setOpenMenu(null);
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {visibleListings.length > 0 ? (
        <div className="listing-grid explore-results-grid" key={`${searchParamsKey || 'all'}-${currentPage}`}>
          {visibleListings.map((listing, index) => (
            <Fragment key={listing.id}>
              <ListingCard listing={listing} />
              {currentPage === 1 && index === 0 ? <BuilderCtaCard key="builder-cta" /> : null}
            </Fragment>
          ))}
        </div>
      ) : (
        <div className="explore-empty-state">
          <h2>No services match these filters</h2>
          <p>Adjust the search, category, or budget filters to broaden the results.</p>
          <button type="button" className="filter-apply-button" onClick={resetExploreSearch}>
            Clear search and filters
          </button>
        </div>
      )}

      {pageCount > 1 ? (
        <nav className="explore-pagination" aria-label="Explore results pagination">
          <button
            type="button"
            className="pagination-button pagination-arrow"
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            <ChevronLeft size={18} />
          </button>

          <div className="pagination-pages">
            {pageItems.map((item) =>
              typeof item === 'number' ? (
                <button
                  key={item}
                  type="button"
                  className={`pagination-button${item === currentPage ? ' active' : ''}`}
                  onClick={() => goToPage(item)}
                  aria-current={item === currentPage ? 'page' : undefined}
                >
                  {item}
                </button>
              ) : (
                <span key={item} className="pagination-gap" aria-hidden="true">
                  ...
                </span>
              )
            )}
          </div>

          <button
            type="button"
            className="pagination-button pagination-arrow"
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === pageCount}
            aria-label="Next page"
          >
            <ChevronRight size={18} />
          </button>

          <span className="pagination-status">
            Page {currentPage} of {pageCount}
          </span>
        </nav>
      ) : null}

      {selectedCategorySlugs.length > 0 || budget !== 'all' || budgetMax !== null ? (
        <div className="explore-clear-row">
          <button type="button" className="text-button" onClick={clearAllFilters}>
            Clear all filters
          </button>
        </div>
      ) : null}
    </section>
  );
}
